import { router } from '@/router/router'
import { URLS } from '@/router/routes/urlRoutes'

import { AxiosError, AxiosRequestConfig } from 'axios'
import { lexonHttp } from '../lexonHttp'
import { useAuthStore } from '@/stores/auth/useAuthStore'
import { getError } from './errors'

let failedQueue: { resolve: (token: string) => void; reject: (reason: AxiosError) => void }[] = []
let isRefreshing: boolean = false

export async function onUnauthorizedRequest(error: any) {
  const originalRequest: AxiosRequestConfig = error.config
  if (error.response.status === 401 && originalRequest.url?.includes('/refresh-session') && isRefreshing) {
    return cleanAndReject(error)
  }

  if (isRefreshing) {
    return saveRequest(originalRequest)
  }

  return retryRequestWithNewToken(originalRequest)
}

const cleanAndReject = (error: any) => {
  processQueue(error, null)
  logout()
  return Promise.reject(getError(error))
}

const processQueue = async (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else if (token) {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

const saveRequest = async (originalRequest: any) => {
  try {
    const token = await new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject })
    })
    originalRequest.headers = originalRequest.headers ?? {}
    originalRequest.headers['Authorization'] = `Bearer ${token}`
    return lexonHttp(originalRequest)
  } catch (err) {
    return Promise.reject(getError(err))
  }
}

const retryRequestWithNewToken = async (originalRequest: any) => {
  isRefreshing = true
  try {
    const token = await refreshToken()
    originalRequest.headers = originalRequest.headers ?? {}
    originalRequest.headers['Authorization'] = `Bearer ${token}`
    await processQueue(null, token)
    return lexonHttp(originalRequest)
  } catch (err) {
    const e = err as AxiosError
    await processQueue(e, null)
    return Promise.reject(getError(e))
  } finally {
    isRefreshing = false
  }
}

const refreshToken = async () => {
  const authStore = useAuthStore()
  await authStore.getRefreshToken()
  if (authStore.error) {
    throw new Error('Error refreshing token')
  }
  return authStore.refreshToken
}

const logout = () => {
  if (router.currentRoute.name === URLS.LOGOUT) {
    return
  }
  router.push({ name: URLS.LOGOUT })
}
